import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OpenAI from 'openai';
import './InputPage.css'; // 导入独立的CSS文件

function InputPage({ onConditionsGenerated }) {
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  // 创建 OpenAI 实例
  const openai = new OpenAI({
    apiKey: process.env.REACT_APP_DEEPSEEK_API_KEY,
    dangerouslyAllowBrowser: true,
    baseURL: "https://api.deepseek.com"
  });

  // 调用 DeepSeek API
  async function callDeepSeek(text) {
    console.log("调用DeepSeek API，输入:", text);
    
    try {
      const response = await openai.chat.completions.create({
        model: "deepseek-chat",
        response_format: { type: "json_object" }, // 启用JSON输出模式
        messages: [
          {
            role: "system",
            content: `你是一个帮助用户解析对伴侣要求的助手。请根据用户输入，提取出用户对伴侣的具体要求，并严格按照以下JSON格式输出：
            
            {
              "appearance": "对外貌的要求，可选值:  '至少不难看', '必须好看'",
              "skin": "对皮肤的要求，可选值:  '白皙', '很好'",
              "height_min": "最低身高要求(cm)，整数",
              "height_max": "最高身高要求(cm)，整数",
              "age_min": "最小年龄要求，整数",
              "age_max": "最大年龄要求，整数",
              "personality": "性格要求，可选值: 'i'(内向), 'e'(外向)",
              "annual_income_min": "最低年收入要求(万元)，整数",
              "property": "对房产的要求，可选值: '必须有房'",
              "education_min": "最低学历要求，可选值: '高中', '本科', '硕士', '博士'",
              "parent_education_min": "对方父母的最低学历要求，可选值: '高中', '本科', '硕士', '博士'",
              "drinking": "对饮酒习惯的要求，可选值: '能接受偶尔', '只接受从不'",
              "smoking": "对抽烟习惯的要求，可选值: '能接受偶尔', '只接受从不'"
            }
            
            注意事项：
            1. 对于"无所谓"、"都行"、"any"、"不限"这类没有明确要求的条件，不要包含在输出中，因为这意味着用户并不将此作为筛选要求。
            2. 只包含用户明确提出的要求，不要包含未提及的字段。
            3. 确保输出的JSON格式完全正确。`
          },
          { 
            role: "user", 
            content: `请解析以下对伴侣的要求，并输出符合上述格式的JSON: ${text}` 
          }
        ],
        temperature: 0.3 // 降低随机性，使输出更稳定
      });

      // 解析返回的JSON结果
      const jsonResponse = response.choices[0].message.content;
      console.log("DeepSeek解析结果:", jsonResponse);
      
      try {
        const parsedConditions = JSON.parse(jsonResponse);
        return parsedConditions;
      } catch (parseError) {
        console.error("JSON解析失败:", parseError);
        throw new Error("解析结果格式不正确");
      }
    } catch (error) {
      console.error("DeepSeek API调用失败:", error);
      throw error;
    }
  }

  async function processInput() {
    if (!userInput.trim()) return;

    setIsLoading(true);
    setErrorMessage('');

    try {
      // 调用DeepSeek API
      const rawConditions = await callDeepSeek(userInput);
      
      // 将生成的条件传递给父组件
      onConditionsGenerated(rawConditions);
      
      // 导航到标签页
      navigate('/tags');

    } catch (error) {
      console.error("处理输入时出错:", error);
      setErrorMessage(error.message || '处理需求时发生错误。请检查您的API密钥或网络连接。');
    } finally {
      setIsLoading(false);
    }
  }

  // 添加默认条件处理函数
  const useDemoConditions = () => {
    const demoConditions = {
      appearance: "至少不难看",
      height_min: 170,
      age_min: 28,
      age_max: 35,
      annual_income_min: 30,
      education_min: "本科",
      smoking: "只接受从不"
    };
    
    // 传递生成的条件并导航
    onConditionsGenerated(demoConditions);
    navigate('/tags');
  };

  return (
    <div className="app-container centered-container">
      <div className="input-content">
        <h1 className="title">请输入您对另一半的需求</h1>
        <p className="prompt-guide">
          (例如：希望对方年龄在28到35岁之间，身高175cm以上，本科及以上学历，年收入至少30万，不抽烟，偶尔喝酒可以接受，性格外向一些比较好，长相至少不难看。)
        </p>

        <div className="input-section">
          <textarea
            className="text-area"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="在这里输入您的需求..."
            rows="5"
            disabled={isLoading}
          />
          <div className="button-group">
            <button 
              className="button"
              onClick={processInput} 
              disabled={isLoading || !userInput.trim()}
            >
              {isLoading ? "处理中..." : "🔍 分析需求"}
            </button>
            <button
              className="button demo-button"
              onClick={useDemoConditions}
              disabled={isLoading}
            >
              ⚡ 使用默认条件
            </button>
          </div>
        </div>

        {errorMessage && <div className="error-message">错误: {errorMessage}</div>}
      </div>
    </div>
  );
}

export default InputPage;